const VIN_YEAR_CODES = 'ABCDEFGHJKLMNPRSTVWXY123456789';
const VIN_CODE_PATTERN = /^[A-Z0-9]+$/;
const FORBIDDEN_VIN_CHARS = /[IOQ]/;
const VIN_SAFE_CHAR_CLASS = '[A-HJ-NPR-Z0-9]';
const YEAR_MIN = 1980;
const YEAR_MAX = 2039;
const PROMPT_LANGUAGE_PATTERN = /^[A-Z0-9_-]{1,16}$/;
const JMC_PROMPT_LANGUAGES = new Set(['CN', 'EN']);

export const DEFAULT_CHECK_DIGIT_PATTERN = '^[0-9X]$';
export const DEFAULT_SERIAL_TAIL_PATTERN = '^[0-9]{5}$';

export function parseTagText(text) {
  return normalizeCodeList(text).values;
}

export function normalizeCodeList(input, validator) {
  const rawItems = Array.isArray(input)
    ? input
    : String(input || '').split(/[\s,，;；]+/);
  const values = [];
  const invalidItems = [];
  const duplicateItems = [];
  const seen = new Set();
  const duplicateSeen = new Set();

  rawItems.forEach((item) => {
    const value = String(item || '').trim().toUpperCase();
    if (!value) {
      return;
    }

    const message = typeof validator === 'function' ? validator(value) : '';
    if (message) {
      invalidItems.push({ value, message });
      return;
    }

    if (seen.has(value)) {
      if (!duplicateSeen.has(value)) {
        duplicateItems.push(value);
        duplicateSeen.add(value);
      }
      return;
    }

    seen.add(value);
    values.push(value);
  });

  return {
    values,
    invalidItems,
    duplicateItems,
  };
}

function validateVinSegment(value, size, label) {
  const code = String(value || '').trim().toUpperCase();
  if (code.length !== size) {
    return `${label}必须是 ${size} 位。`;
  }
  if (!VIN_CODE_PATTERN.test(code)) {
    return `${label}只能包含大写字母或数字。`;
  }
  if (FORBIDDEN_VIN_CHARS.test(code)) {
    return `${label}不能包含 I、O、Q。`;
  }
  return '';
}

export function validateManufacturerCode(value) {
  return validateVinSegment(value, 3, '厂商识别码');
}

export function validateVehicleDescriptionCode(value) {
  return validateVinSegment(value, 5, '车辆描述码');
}

export function validateAssemblyPlantCode(value) {
  return validateVinSegment(value, 1, '装配厂代码');
}

export function validateSerial12Code(value) {
  return validateVinSegment(value, 1, '流水号首位');
}

export function normalizeVinInput(text) {
  return normalizeCodeList(text).values.filter((vin) => vin.length === 17);
}

export function normalizeVin(value) {
  return String(value || '').trim().toUpperCase();
}

export function sortVinCodesAscending(values = []) {
  return [...values].sort((left, right) => String(left).localeCompare(String(right), 'en'));
}

function numericPriority(rule) {
  const priority = Number(rule?.priority);
  return Number.isFinite(priority) ? priority : 0;
}

export function sortRulesByPriorityDesc(rules = []) {
  return [...(Array.isArray(rules) ? rules : [])].sort((left, right) => {
    const priorityDiff = numericPriority(right) - numericPriority(left);
    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    const leftName = String(left?.name || left?.id || '');
    const rightName = String(right?.name || right?.id || '');
    const nameDiff = leftName.localeCompare(rightName, 'zh-Hans-CN');
    if (nameDiff !== 0) {
      return nameDiff;
    }
    return String(left?.id || '').localeCompare(String(right?.id || ''), 'en');
  });
}

export function modelYearCode(year) {
  const numericYear = Number(year);
  if (!Number.isInteger(numericYear) || numericYear < YEAR_MIN) {
    return '';
  }

  return VIN_YEAR_CODES[(numericYear - YEAR_MIN) % VIN_YEAR_CODES.length] || '';
}

export function buildYearCodeEntries(from, to) {
  const start = Number(from);
  const end = Number(to);

  if (!Number.isInteger(start) || !Number.isInteger(end) || start > end) {
    return [];
  }

  const boundedStart = Math.max(YEAR_MIN, start);
  const boundedEnd = Math.min(YEAR_MAX, end);
  const entries = [];

  for (let year = boundedStart; year <= boundedEnd; year += 1) {
    const code = modelYearCode(year);
    if (code) {
      entries.push({ year, code });
    }
  }

  return entries;
}

export function buildYearCodeMap(from, to) {
  return buildYearCodeEntries(from, to).reduce((map, item) => {
    map[String(item.year)] = item.code;
    return map;
  }, {});
}

export function defaultAssemblyPlants() {
  return [
    { code: 'T', name: '全顺汽车厂', selected: true, builtin: true },
    { code: 'H', name: '江铃汽车有限公司', selected: true, builtin: true },
  ];
}

export function defaultEditableVinRule() {
  return {
    id: 'jiangling-vin-filter',
    name: '江铃 VIN 过滤模板',
    remark: '',
    enabled: true,
    priority: 100,
    vdsPolicy: 'byWmiAllowList',
    allowAnyVdsWhenListMissing: false,
    wmiAllowList: ['LJX'],
    vdsAllowListByWmi: {
      LJX: ['CU2BB', 'CU2BM', 'CU2BN', 'CU2BP'],
      LEF: [],
    },
    checkDigitPattern: DEFAULT_CHECK_DIGIT_PATTERN,
    modelYearRange: {
      from: 2005,
      to: 2031,
    },
    yearCodeMap: buildYearCodeMap(2005, 2031),
    plantAllowList: ['T', 'H'],
    plantNameMap: {
      T: '全顺汽车厂',
      H: '江铃汽车有限公司',
    },
    serial12: {
      enabled: false,
      allowList: [],
    },
    serialTailPattern: DEFAULT_SERIAL_TAIL_PATTERN,
    jmcPromptTextByVds: {},
  };
}

function normalizeVehicleDescriptionMap(sourceMap, manufacturerCodes) {
  const map = {};
  const rawMap = sourceMap && typeof sourceMap === 'object' ? sourceMap : {};
  const allManufacturers = [...new Set([...manufacturerCodes, ...Object.keys(rawMap).map((key) => key.trim().toUpperCase())])];

  allManufacturers.forEach((manufacturerCode) => {
    if (!manufacturerCode) {
      return;
    }
    map[manufacturerCode] = sortVinCodesAscending(
      normalizeCodeList(rawMap[manufacturerCode] || [], validateVehicleDescriptionCode).values
    );
  });

  return map;
}

function normalizePromptLanguageCode(value) {
  const code = String(value || '').trim().toUpperCase();
  return PROMPT_LANGUAGE_PATTERN.test(code) ? code : '';
}

function normalizeJmcPromptTextByVds(sourceMap, vehicleDescriptionByManufacturer = {}) {
  const promptMap = {};
  const rawMap = sourceMap && typeof sourceMap === 'object' ? sourceMap : {};

  Object.entries(vehicleDescriptionByManufacturer).forEach(([rawManufacturerCode, vehicleDescriptions]) => {
    const manufacturerCode = String(rawManufacturerCode || '').trim().toUpperCase();
    if (!manufacturerCode || !Array.isArray(vehicleDescriptions)) {
      return;
    }

    const rawByManufacturer = rawMap[manufacturerCode] || rawMap[rawManufacturerCode] || {};
    if (!rawByManufacturer || typeof rawByManufacturer !== 'object') {
      return;
    }

    vehicleDescriptions.forEach((rawVehicleDescriptionCode) => {
      const vehicleDescriptionCode = String(rawVehicleDescriptionCode || '').trim().toUpperCase();
      if (!vehicleDescriptionCode) {
        return;
      }

      const rawByVds = rawByManufacturer[vehicleDescriptionCode] || rawByManufacturer[rawVehicleDescriptionCode] || {};
      if (!rawByVds || typeof rawByVds !== 'object') {
        return;
      }

      const languageMap = {};
      Object.entries(rawByVds).forEach(([rawLanguage, rawText]) => {
        const language = normalizePromptLanguageCode(rawLanguage);
        const text = String(rawText || '').trim();
        if (JMC_PROMPT_LANGUAGES.has(language) && text) {
          languageMap[language] = text;
        }
      });

      if (Object.keys(languageMap).length) {
        if (!promptMap[manufacturerCode]) {
          promptMap[manufacturerCode] = {};
        }
        promptMap[manufacturerCode][vehicleDescriptionCode] = languageMap;
      }
    });
  });

  return promptMap;
}

function normalizeAssemblyPlants(rule) {
  const defaultNames = new Map(defaultAssemblyPlants().map((item) => [item.code, item.name]));
  const known = new Map();
  const rawNameMap = rule.plantNameMap && typeof rule.plantNameMap === 'object' ? rule.plantNameMap : {};
  const selectedCodes = normalizeCodeList(rule.plantAllowList || [], validateAssemblyPlantCode).values;

  selectedCodes.forEach((code) => {
    known.set(code, {
      code,
      name: rawNameMap[code] || defaultNames.get(code) || code,
      selected: true,
      builtin: defaultNames.has(code),
    });
  });

  return sortVinCodesAscending([...known.keys()]).map((code) => known.get(code));
}

export function ruleToEditableForm(rule = {}) {
  const defaults = defaultEditableVinRule();
  const source = {
    ...defaults,
    ...rule,
    modelYearRange: {
      ...defaults.modelYearRange,
      ...(rule.modelYearRange || {}),
    },
    serial12: {
      ...defaults.serial12,
      ...(rule.serial12 || {}),
    },
    vdsAllowListByWmi: {
      ...defaults.vdsAllowListByWmi,
      ...(rule.vdsAllowListByWmi || {}),
    },
    plantNameMap: {
      ...defaults.plantNameMap,
      ...(rule.plantNameMap || {}),
    },
  };
  const manufacturerCodes = normalizeCodeList(source.wmiAllowList || [], validateManufacturerCode).values;
  const vehicleDescriptionByManufacturer = normalizeVehicleDescriptionMap(source.vdsAllowListByWmi, manufacturerCodes);

  return {
    id: source.id || '',
    name: source.name || '',
    remark: source.remark || '',
    enabled: source.enabled !== false,
    priority: Number.isFinite(Number(source.priority)) ? Number(source.priority) : 100,
    manufacturerCodes,
    vehicleDescriptionByManufacturer,
    jmcPromptTextByVds: normalizeJmcPromptTextByVds(source.jmcPromptTextByVds, vehicleDescriptionByManufacturer),
    assemblyPlants: normalizeAssemblyPlants(source),
    yearFrom: Number(source.modelYearRange?.from) || 2005,
    yearTo: Number(source.modelYearRange?.to) || 2031,
    allowAnyVdsWhenListMissing: source.allowAnyVdsWhenListMissing === true,
    checkDigitPattern: source.checkDigitPattern || DEFAULT_CHECK_DIGIT_PATTERN,
    serial12Enabled: source.serial12?.enabled === true,
    serial12AllowList: normalizeCodeList(source.serial12?.allowList || [], validateSerial12Code).values,
    serialTailPattern: source.serialTailPattern || DEFAULT_SERIAL_TAIL_PATTERN,

    // Backward-compatible fields for older tests or callers.
    wmiText: manufacturerCodes.join('\n'),
    ljxVdsText: (source.vdsAllowListByWmi?.LJX || []).join('\n'),
    lefVdsText: (source.vdsAllowListByWmi?.LEF || []).join('\n'),
    plantText: (source.plantAllowList || []).join(','),
    serial12Text: (source.serial12?.allowList || []).join(','),
  };
}

function selectedAssemblyPlants(form) {
  if (Array.isArray(form.assemblyPlants)) {
    return form.assemblyPlants
      .map((item) => ({
        code: String(item?.code || '').trim().toUpperCase(),
        name: String(item?.name || '').trim(),
        selected: item?.selected !== false,
      }))
      .filter((item) => item.selected);
  }

  if (Array.isArray(form.plantAllowList)) {
    const nameMap = form.plantNameMap && typeof form.plantNameMap === 'object' ? form.plantNameMap : {};
    return normalizeCodeList(form.plantAllowList, validateAssemblyPlantCode).values.map((code) => ({
      code,
      name: String(nameMap[code] || code).trim(),
      selected: true,
    }));
  }

  return normalizeCodeList(form.plantText, validateAssemblyPlantCode).values.map((code) => ({
    code,
    name: code,
    selected: true,
  }));
}

function vehicleDescriptionMapFromForm(form, manufacturerCodes) {
  if (form.vehicleDescriptionByManufacturer && typeof form.vehicleDescriptionByManufacturer === 'object') {
    return normalizeVehicleDescriptionMap(form.vehicleDescriptionByManufacturer, manufacturerCodes);
  }

  if (form.vdsAllowListByWmi && typeof form.vdsAllowListByWmi === 'object') {
    return normalizeVehicleDescriptionMap(form.vdsAllowListByWmi, manufacturerCodes);
  }

  return normalizeVehicleDescriptionMap({
    LJX: form.ljxVdsText,
    LEF: form.lefVdsText,
  }, manufacturerCodes);
}

export function buildVinRulePayload(form = {}) {
  const yearFrom = Number(form.yearFrom ?? form.modelYearRange?.from);
  const yearTo = Number(form.yearTo ?? form.modelYearRange?.to);
  const safeYearFrom = Number.isInteger(yearFrom) ? yearFrom : 2005;
  const safeYearTo = Number.isInteger(yearTo) ? yearTo : safeYearFrom;
  const manufacturerSource = form.manufacturerCodes || form.wmiAllowList || form.wmiText;
  const manufacturerCodes = normalizeCodeList(manufacturerSource, validateManufacturerCode).values;
  const vehicleDescriptionByManufacturer = vehicleDescriptionMapFromForm(form, manufacturerCodes);
  const jmcPromptTextByVds = normalizeJmcPromptTextByVds(form.jmcPromptTextByVds, vehicleDescriptionByManufacturer);
  const plants = selectedAssemblyPlants(form);
  const plantAllowList = normalizeCodeList(plants.map((item) => item.code), validateAssemblyPlantCode).values;
  const plantNameMap = {};

  plants.forEach((item) => {
    if (plantAllowList.includes(item.code)) {
      plantNameMap[item.code] = item.name || item.code;
    }
  });

  const serial12Source = form.serial12AllowList || form.serial12?.allowList || form.serial12Text;
  const serial12Enabled = form.serial12Enabled === true || form.serial12?.enabled === true;

  return {
    id: String(form.id || '').trim(),
    name: String(form.name || '').trim(),
    enabled: form.enabled !== false,
    priority: Number.isFinite(Number(form.priority)) ? Number(form.priority) : 100,
    remark: String(form.remark || '').trim(),
    wmiAllowList: manufacturerCodes,
    vdsPolicy: 'byWmiAllowList',
    allowAnyVdsWhenListMissing: form.allowAnyVdsWhenListMissing === true,
    vdsAllowListByWmi: vehicleDescriptionByManufacturer,
    checkDigitPattern: String(form.checkDigitPattern || DEFAULT_CHECK_DIGIT_PATTERN).trim(),
    modelYearRange: {
      from: safeYearFrom,
      to: safeYearTo,
    },
    yearCodeMap: buildYearCodeMap(safeYearFrom, safeYearTo),
    plantAllowList,
    plantNameMap,
    serial12: {
      enabled: serial12Enabled,
      allowList: normalizeCodeList(serial12Source, validateSerial12Code).values,
    },
    serialTailPattern: String(form.serialTailPattern || DEFAULT_SERIAL_TAIL_PATTERN).trim(),
    jmcPromptTextByVds,
  };
}

export function findJmcPromptText(rawRule = {}, vinInput = '', language = '') {
  const rule = buildVinRulePayload(rawRule);
  const vin = normalizeVin(vinInput);
  if (!testVinAgainstRule(vin, rule).passed) {
    return '';
  }

  const manufacturerCode = vin.slice(0, 3);
  const vehicleDescriptionCode = vin.slice(3, 8);
  const languageMap = rule.jmcPromptTextByVds?.[manufacturerCode]?.[vehicleDescriptionCode] || {};
  const targetLanguage = normalizePromptLanguageCode(language) === 'CN' ? 'CN' : 'EN';
  return String(languageMap[targetLanguage] || '').trim();
}

export function allowedYearCodesFromRule(rule = {}) {
  const map = rule.yearCodeMap || {};
  const range = rule.modelYearRange || {};
  const from = Number(range.from);
  const to = Number(range.to);
  const years = Object.keys(map)
    .map((year) => Number(year))
    .filter((year) => Number.isInteger(year))
    .filter((year) => (!Number.isInteger(from) || year >= from) && (!Number.isInteger(to) || year <= to))
    .sort((a, b) => a - b);

  return [...new Set(years.map((year) => String(map[String(year)] || '').trim()).filter(Boolean))];
}

export function yearCodeEntriesFromRule(rule = {}) {
  const map = rule.yearCodeMap || {};
  const range = rule.modelYearRange || {};
  return buildYearCodeEntries(Number(range.from), Number(range.to)).map((item) => ({
    year: item.year,
    code: map[String(item.year)] || item.code,
  }));
}

function stripAnchors(pattern) {
  return String(pattern || '')
    .replace(/^\^/, '')
    .replace(/\$$/, '');
}

function escapeRegex(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function codeAlternation(values, fallback = '(?!)') {
  const list = normalizeCodeList(values).values;
  if (!list.length) {
    return fallback;
  }
  return list.map(escapeRegex).join('|');
}

export function buildVinRegex(rule = {}) {
  const payload = buildVinRulePayload(rule);
  const manufacturerVehicleParts = payload.wmiAllowList.map((manufacturerCode) => {
    const vehicleDescriptions = payload.vdsAllowListByWmi[manufacturerCode] || [];
    if (vehicleDescriptions.length) {
      return `${escapeRegex(manufacturerCode)}(?:${codeAlternation(vehicleDescriptions)})`;
    }
    if (payload.allowAnyVdsWhenListMissing) {
      return `${escapeRegex(manufacturerCode)}${VIN_SAFE_CHAR_CLASS}{5}`;
    }
    return '';
  }).filter(Boolean);
  const manufacturerVehiclePattern = manufacturerVehicleParts.length
    ? `(?:${manufacturerVehicleParts.join('|')})`
    : '(?!)';
  const yearPattern = `(?:${codeAlternation(allowedYearCodesFromRule(payload))})`;
  const plantPattern = `(?:${codeAlternation(payload.plantAllowList)})`;
  const serial12Pattern = payload.serial12.enabled
    ? `(?:${codeAlternation(payload.serial12.allowList)})`
    : `${VIN_SAFE_CHAR_CLASS}`;

  return `^${manufacturerVehiclePattern}${stripAnchors(payload.checkDigitPattern)}${yearPattern}${plantPattern}${serial12Pattern}${stripAnchors(payload.serialTailPattern)}$`;
}

function findYearByCode(rule, code) {
  const entries = yearCodeEntriesFromRule(rule);
  const matched = entries.find((item) => item.code === code);
  return matched?.year || '';
}

function pushItem(items, errors, item) {
  items.push(item);
  if (!item.passed) {
    const message = String(item.message || '');
    errors.push(message.startsWith(item.name)
      ? `${item.position}${message}`
      : `${item.position}${item.name}${message}`);
  }
}

export function testVinAgainstRule(vinInput, rawRule = {}) {
  const rule = buildVinRulePayload(rawRule);
  const vin = normalizeVin(vinInput);
  const items = [];
  const errors = [];
  const invalidVinShape = vin.length !== 17 || FORBIDDEN_VIN_CHARS.test(vin) || !VIN_CODE_PATTERN.test(vin);

  if (vin.length !== 17) {
    errors.push('VIN 必须是 17 位。');
  }
  if (FORBIDDEN_VIN_CHARS.test(vin)) {
    errors.push('VIN 不能包含 I、O、Q。');
  }
  if (vin && !VIN_CODE_PATTERN.test(vin)) {
    errors.push('VIN 只能包含大写字母或数字。');
  }

  const manufacturerCode = vin.slice(0, 3);
  const vehicleDescriptionCode = vin.slice(3, 8);
  const checkDigit = vin.slice(8, 9);
  const yearCode = vin.slice(9, 10);
  const plantCode = vin.slice(10, 11);
  const serial12 = vin.slice(11, 12);
  const serialTail = vin.slice(12);

  const manufacturerPassed = !invalidVinShape && rule.wmiAllowList.includes(manufacturerCode);
  pushItem(items, errors, {
    position: '第 1-3 位',
    name: '厂商识别码',
    value: manufacturerCode,
    passed: manufacturerPassed,
    message: manufacturerPassed ? '命中' : `厂商识别码 ${manufacturerCode || '-'} 不在白名单中。`,
  });

  const vehicleList = rule.vdsAllowListByWmi[manufacturerCode] || [];
  const vehiclePassed = !invalidVinShape && (
    vehicleList.includes(vehicleDescriptionCode) ||
    (vehicleList.length === 0 && rule.allowAnyVdsWhenListMissing)
  );
  pushItem(items, errors, {
    position: '第 4-8 位',
    name: '车辆描述码',
    value: vehicleDescriptionCode,
    passed: vehiclePassed,
    message: vehiclePassed
      ? (vehicleList.length ? '命中' : '当前厂商识别码下允许空列表放行')
      : `车辆描述码 ${vehicleDescriptionCode || '-'} 不在 ${manufacturerCode || '-'} 的白名单中。`,
  });

  const checkPassed = !invalidVinShape && new RegExp(stripAnchors(rule.checkDigitPattern)).test(checkDigit);
  pushItem(items, errors, {
    position: '第 9 位',
    name: '校验位',
    value: checkDigit,
    passed: checkPassed,
    message: checkPassed ? '合法' : '不合法，应为数字 0-9 或 X',
  });

  const year = findYearByCode(rule, yearCode);
  const yearPassed = !invalidVinShape && Boolean(year);
  pushItem(items, errors, {
    position: '第 10 位',
    name: '年款码',
    value: yearCode,
    passed: yearPassed,
    message: yearPassed ? `对应 ${year}，命中` : `年款码 ${yearCode || '-'} 不在允许范围内。`,
  });

  const plantPassed = !invalidVinShape && rule.plantAllowList.includes(plantCode);
  const plantName = rule.plantNameMap?.[plantCode] || '';
  pushItem(items, errors, {
    position: '第 11 位',
    name: '装配厂代码',
    value: plantCode,
    passed: plantPassed,
    message: plantPassed ? `${plantName || plantCode}，命中` : `装配厂代码 ${plantCode || '-'} 不在允许范围内。`,
  });

  const serial12Passed = !invalidVinShape && (
    !rule.serial12.enabled || rule.serial12.allowList.includes(serial12)
  );
  pushItem(items, errors, {
    position: '第 12 位',
    name: '流水号首位',
    value: serial12,
    passed: serial12Passed,
    message: rule.serial12.enabled
      ? (serial12Passed ? '命中' : `流水号首位 ${serial12 || '-'} 不在允许范围内。`)
      : '当前未启用限制',
  });

  const tailPassed = !invalidVinShape && new RegExp(stripAnchors(rule.serialTailPattern)).test(serialTail);
  pushItem(items, errors, {
    position: '第 13-17 位',
    name: '流水号后五位',
    value: serialTail,
    passed: tailPassed,
    message: tailPassed ? '合法' : '不合法，应为 5 位数字',
  });

  return {
    passed: items.every((item) => item.passed),
    status: items.every((item) => item.passed) ? 'passed' : 'failed',
    vin,
    items,
    errors: [...new Set(errors)],
  };
}

export function summarizeVinTestRow(row = {}) {
  const vin = row.vin || row.normalizedVin || '-';
  if (row.passed) {
    return `${vin} 通过`;
  }
  if (row.matched) {
    return `${vin} 命中 ${row.matchedRuleName || row.matchedRuleId || '规则'}`;
  }

  return `${vin} 未命中：${row.reason || row.errors?.[0] || '未匹配任何规则'}`;
}
